import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Building2, Home as HomeIcon, MapPin, Maximize, BedDouble, Euro, Filter, Search, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import Layout from "@/components/Layout";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const catalogueLabels = {
  investissement: "Investissement Locatif",
  residence_principale: "Résidence Principale",
  accession_aidee: "Accession Aidée",
};

const dispositifLabels = {
  denormandie: "Denormandie",
  jeanbrun: "Jeanbrun",
  lmnp: "LMNP",
  locavantages: "Loc'Avantages",
  malraux: "Malraux",
  monuments_historiques: "Monuments Hist.",
  deficit_foncier: "Déficit Foncier",
  brs: "BRS",
  psla: "PSLA",
  tva_reduite: "TVA 5.5%",
};

const dispositifColors = {
  denormandie: "bg-green-100 text-green-700",
  jeanbrun: "bg-blue-100 text-blue-700",
  lmnp: "bg-purple-100 text-purple-700",
  locavantages: "bg-orange-100 text-orange-700",
  malraux: "bg-amber-100 text-amber-700",
  monuments_historiques: "bg-rose-100 text-rose-700",
  deficit_foncier: "bg-slate-100 text-slate-700",
  brs: "bg-teal-100 text-teal-700",
  psla: "bg-cyan-100 text-cyan-700",
  tva_reduite: "bg-yellow-100 text-yellow-700",
};

const Catalogue = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  // Filters
  const [catalogue, setCatalogue] = useState(searchParams.get("catalogue") || "all");
  const [dispositif, setDispositif] = useState(searchParams.get("dispositif") || "all");
  const [ville, setVille] = useState(searchParams.get("ville") || "");
  const [typeBien, setTypeBien] = useState("all");
  const [prixMax, setPrixMax] = useState("");

  useEffect(() => {
    fetchProperties();
  }, [catalogue, dispositif, ville, typeBien, prixMax]);

  const fetchProperties = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (catalogue && catalogue !== "all") params.append("catalogue", catalogue);
      if (dispositif && dispositif !== "all") params.append("dispositif", dispositif);
      if (ville) params.append("ville", ville);
      if (typeBien && typeBien !== "all") params.append("type_bien", typeBien);
      if (prixMax) params.append("prix_max", prixMax);

      const response = await axios.get(`${API}/properties?${params.toString()}`);
      setProperties(response.data);
    } catch (error) {
      console.error("Error fetching properties:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
    }).format(price);
  };

  const openDetail = (property) => {
    setSelectedProperty(property);
    setShowDetail(true);
  };

  const PropertyCard = ({ property }) => {
    return (
      <Card 
        className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
        onClick={() => openDetail(property)}
        data-testid={`property-card-${property.id}`}
      >
        <div className="relative">
          <img
            src={property.image_url || "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800"}
            alt={property.titre}
            className="w-full h-48 object-cover"
          />
          <div className="absolute top-2 right-2 flex flex-wrap gap-1 max-w-[70%] justify-end">
            {property.dispositif_eligible?.slice(0, 2).map((disp) => (
              <Badge key={disp} className={`text-xs ${dispositifColors[disp] || "bg-gray-100 text-gray-700"}`}>
                {dispositifLabels[disp] || disp}
              </Badge>
            ))}
          </div>
          <div className="absolute bottom-2 left-2">
            <Badge className="bg-harbor text-white font-bold text-lg px-3 py-1">
              {formatPrice(property.prix)}
            </Badge>
          </div>
        </div>
        <CardContent className="p-4">
          <h3 className="font-heading text-lg text-harbor mb-2 line-clamp-1">{property.titre}</h3>
          <div className="flex items-center gap-1 text-muted-foreground text-sm mb-3">
            <MapPin size={14} />
            <span>{property.ville} ({property.code_postal})</span>
          </div>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <Maximize size={14} />
              <span>{property.surface} m²</span>
            </div>
            <div className="flex items-center gap-1">
              {property.type_bien === "maison" ? <HomeIcon size={14} /> : <Building2 size={14} />}
              <span>T{property.nb_pieces}</span>
            </div>
            <div className="flex items-center gap-1">
              <BedDouble size={14} />
              <span>{property.nb_chambres} ch.</span>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t flex items-center justify-between">
            <Badge variant="outline" className="text-xs">
              DPE {property.dpe}
            </Badge>
            <span className="text-xs text-muted-foreground capitalize">
              {property.type_bien}
            </span>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <Layout>
      {/* Hero */}
      <section className="bg-harbor py-16" data-testid="catalogue-hero">
        <div className="container-custom">
          <div className="max-w-3xl text-white">
            <h1 className="font-heading text-4xl sm:text-5xl font-bold mb-4 animate-fade-in-up">
              Catalogue de Biens
            </h1>
            <p className="text-lg text-white/80 animate-fade-in-up animation-delay-200">
              Découvrez notre sélection de biens immobiliers à Nantes et en Loire-Atlantique
            </p>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-white border-b sticky top-16 z-40" data-testid="catalogue-filters">
        <div className="container-custom py-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <Filter size={18} className="text-muted-foreground" />
              <span className="text-sm font-medium">Filtres:</span>
            </div>
            
            <Select value={catalogue} onValueChange={setCatalogue}>
              <SelectTrigger className="w-48" data-testid="filter-catalogue">
                <SelectValue placeholder="Type de catalogue" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les catalogues</SelectItem>
                <SelectItem value="investissement">Investissement Locatif</SelectItem>
                <SelectItem value="residence_principale">Résidence Principale</SelectItem>
                <SelectItem value="accession_aidee">Accession Aidée</SelectItem>
              </SelectContent>
            </Select>

            <Select value={dispositif} onValueChange={setDispositif}>
              <SelectTrigger className="w-40" data-testid="filter-dispositif">
                <SelectValue placeholder="Dispositif" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="denormandie">Denormandie</SelectItem>
                <SelectItem value="jeanbrun">Jeanbrun</SelectItem>
                <SelectItem value="locavantages">Loc'Avantages</SelectItem>
                <SelectItem value="lmnp">LMNP</SelectItem>
                <SelectItem value="malraux">Malraux</SelectItem>
                <SelectItem value="monuments_historiques">Monuments Historiques</SelectItem>
                <SelectItem value="brs">BRS</SelectItem>
                <SelectItem value="psla">PSLA</SelectItem>
              </SelectContent>
            </Select>

            <Select value={typeBien} onValueChange={setTypeBien}>
              <SelectTrigger className="w-36" data-testid="filter-type">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous types</SelectItem>
                <SelectItem value="appartement">Appartement</SelectItem>
                <SelectItem value="maison">Maison</SelectItem>
              </SelectContent>
            </Select>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
              <Input
                placeholder="Ville..."
                value={ville}
                onChange={(e) => setVille(e.target.value)}
                className="pl-9 w-32"
                data-testid="filter-ville"
              />
            </div>

            <div className="relative">
              <Euro className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
              <Input
                placeholder="Prix max"
                value={prixMax}
                onChange={(e) => setPrixMax(e.target.value)}
                className="pl-9 w-32"
                type="number"
                data-testid="filter-prix"
              />
            </div>

            <Button variant="outline" size="sm" onClick={() => {
              setCatalogue("all");
              setDispositif("all");
              setVille("");
              setTypeBien("all");
              setPrixMax("");
            }}>
              Réinitialiser
            </Button>
          </div>
        </div>
      </section>

      {/* Properties Grid */}
      <section className="section-spacing bg-paper" data-testid="catalogue-content">
        <div className="container-custom">
          {loading ? (
            <div className="text-center py-12 text-muted-foreground">
              Chargement des biens...
            </div>
          ) : properties.length === 0 ? (
            <div className="text-center py-12" data-testid="no-properties">
              <Building2 className="mx-auto text-muted-foreground mb-4" size={48} />
              <h3 className="font-heading text-xl text-harbor mb-2">Aucun bien trouvé</h3>
              <p className="text-muted-foreground mb-4">
                Essayez de modifier vos critères de recherche
              </p>
              <Button variant="outline" onClick={() => {
                setCatalogue("all");
                setDispositif("all");
                setVille("");
              }}>
                Voir tous les biens
              </Button>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-6">
                <p className="text-muted-foreground">
                  <span className="font-semibold text-harbor">{properties.length}</span> bien(s) trouvé(s)
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {properties.map((property) => (
                  <PropertyCard key={property.id} property={property} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-harbor/10">
        <div className="container-custom text-center">
          <h2 className="font-heading text-2xl text-harbor mb-4">
            Vous ne trouvez pas le bien idéal ?
          </h2>
          <p className="text-muted-foreground mb-6">
            Contactez-nous pour une recherche personnalisée selon vos critères
          </p>
          <Link to="/contact">
            <Button className="bg-clay hover:bg-clay/90 text-white">
              Nous contacter
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
        </div>
      </section>

      {/* Property Detail Dialog */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="property-detail-dialog">
          {selectedProperty && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl text-harbor">
                  {selectedProperty.titre}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-6">
                <img
                  src={selectedProperty.image_url || "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800"}
                  alt={selectedProperty.titre}
                  className="w-full h-64 object-cover rounded-md"
                />
                
                <div className="flex items-center justify-between">
                  <div className="text-3xl font-heading text-harbor">
                    {formatPrice(selectedProperty.prix)}
                  </div>
                  <div className="flex gap-2">
                    {selectedProperty.dispositif_eligible?.map((disp) => (
                      <Badge key={disp} className={dispositifColors[disp] || "bg-gray-100"}>
                        {dispositifLabels[disp] || disp}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-2 text-muted-foreground">
                  <MapPin size={18} />
                  <span>{selectedProperty.quartier && `${selectedProperty.quartier}, `}{selectedProperty.ville} ({selectedProperty.code_postal})</span>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-md">
                  <div className="text-center">
                    <div className="font-semibold text-harbor">{selectedProperty.surface} m²</div>
                    <div className="text-xs text-muted-foreground">Surface</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold text-harbor">T{selectedProperty.nb_pieces}</div>
                    <div className="text-xs text-muted-foreground">Pièces</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold text-harbor">{selectedProperty.nb_chambres}</div>
                    <div className="text-xs text-muted-foreground">Chambres</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold text-harbor">{selectedProperty.dpe}</div>
                    <div className="text-xs text-muted-foreground">DPE</div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-harbor mb-2">Description</h4>
                  <p className="text-muted-foreground leading-relaxed">
                    {selectedProperty.description}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold text-harbor mb-2">Caractéristiques</h4>
                    <ul className="space-y-1 text-sm text-muted-foreground">
                      <li>Type: {selectedProperty.type_bien}</li>
                      {selectedProperty.etage !== undefined && <li>Étage: {selectedProperty.etage}</li>}
                      {selectedProperty.annee_construction && <li>Année: {selectedProperty.annee_construction}</li>}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-harbor mb-2">Équipements</h4>
                    <ul className="space-y-1 text-sm text-muted-foreground">
                      {selectedProperty.parking && <li>✓ Parking</li>}
                      {selectedProperty.balcon && <li>✓ Balcon</li>}
                      {selectedProperty.terrasse && <li>✓ Terrasse</li>}
                      {selectedProperty.jardin && <li>✓ Jardin</li>}
                      {selectedProperty.ascenseur && <li>✓ Ascenseur</li>}
                    </ul>
                  </div>
                </div>

                <div className="flex gap-4 pt-4 border-t">
                  <Link to="/simulateur" className="flex-1">
                    <Button className="w-full bg-clay hover:bg-clay/90 text-white">
                      Simuler mon investissement
                    </Button>
                  </Link>
                  <Link to="/contact" className="flex-1">
                    <Button variant="outline" className="w-full border-harbor text-harbor">
                      Demander plus d'infos
                    </Button>
                  </Link>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Catalogue;
